/**
 * Fallback SKU display parser.
 *
 * Some SKUs are admin-internal — they live in ops.stock_targets but never
 * get into public.tire_catalog (so customers don't see them in the LIFF) or
 * into ops.sku_meta yet. Stock list and Order/Transfer Day pages then show
 * "—" because the JOIN returns null.
 *
 * This parser derives a best-effort label from the SKU pattern alone, so
 * the operator at least sees something recognizable. The pattern we see in
 * production:
 *
 *   01-XX-MMMM-NNNNNNN
 *
 *   01     fixed prefix
 *   XX     2- to 4-letter brand code  (BS, MC, YK, FS, CT, ...)
 *   MMMM   model code, may include digits and parentheses (T005, POSP, T005(RFT))
 *   NNN... 6–8 digit size code: WWWHHRR  →  WWW/HH-RR  (e.g. 2155517 → 215/55-17)
 *
 * If the regex doesn't match (unusual SKU shape), we leave brand/model null
 * so the caller can fall back to "—".
 */

const BRAND_CODE_TO_NAME: Record<string, string> = {
  // Add codes as you encounter them. Unknown codes pass through verbatim.
  AV: 'อาวอน',
  BS: 'บริดจสโตน',
  CT: 'คอนติเนนทอล',
  DT: 'เดย์ตัน',
  FS: 'ไฟร์สโตน',
  GT: 'ไจแอนท์',
  GY: 'กู๊ดเยียร์',
  HK: 'ฮันคุก',
  KU: 'คุมโฮ',
  MC: 'มิชลิน',
  MX: 'แม็กซิส',
  NK: 'นังกัง',
  PR: 'ไพรเมียร์',
  SY: 'ที.สยาม',
  TY: 'ทอเย่',
  YE: 'ยีดา',
  YK: 'โยโกฮาม่า',
};

const SKU_RE = /^01-([A-Z]{2,4})-([A-Z0-9()._-]+)-(\d{6,8})$/i;

export interface SkuParts {
  brand: string | null;
  model: string | null;
  size: string | null;
}

/**
 * Parse a SKU string into brand/model/size based on the standard pattern.
 * Returns nulls if the pattern doesn't match.
 */
export function parseSkuFallback(sku: string): SkuParts {
  const m = sku.match(SKU_RE);
  if (!m) return { brand: null, model: null, size: null };
  const brandCode = m[1]!.toUpperCase();
  const model = m[2]!;
  const sizeCode = m[3]!;
  return {
    brand: BRAND_CODE_TO_NAME[brandCode] ?? brandCode,
    model,
    size: parseSizeCode(sizeCode),
  };
}

function parseSizeCode(code: string): string | null {
  // 7 digits: WWWHHRR  →  WWW/HH-RR    (e.g. 2155517 → 215/55-17)
  // 8 digits: WWWHHRRR →  WWW/HH-RRR   (rare, very large rim sizes)
  // 6 digits: ambiguous; pass through.
  if (code.length === 7) {
    return `${code.slice(0, 3)}/${code.slice(3, 5)}-${code.slice(5)}`;
  }
  if (code.length === 8) {
    return `${code.slice(0, 3)}/${code.slice(3, 5)}-${code.slice(5)}`;
  }
  return code;
}

/**
 * Resolve display values, preferring the DB-provided ones. Fallback to the
 * SKU pattern parser, then to nulls if nothing matches.
 */
export function resolveSkuDisplay(
  sku: string,
  brand: string | null | undefined,
  model: string | null | undefined,
  size: string | null | undefined
): SkuParts {
  if (brand && model && size) return { brand, model, size };
  const fb = parseSkuFallback(sku);
  return {
    brand: brand ?? fb.brand,
    model: model ?? fb.model,
    size: size ?? fb.size,
  };
}

/**
 * Formatted "Brand Model" for one-line display ("ไฟร์สโตน F01" or "FS F01").
 */
export function formatProduct(
  sku: string,
  brand: string | null | undefined,
  model: string | null | undefined
): string {
  const r = resolveSkuDisplay(sku, brand, model, null);
  return [r.brand, r.model].filter(Boolean).join(' ') || '—';
}
